import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import api from '../utils/api';
import { toast } from 'sonner';
import { ArrowLeft, Building2, Globe, Mail, Phone, MapPin, User, Loader2, Search, CheckCircle2, XCircle, AlertCircle, Users, HardDrive, Grid3x3, UserPlus, DollarSign, Eye } from 'lucide-react';
import ContactsTab from '../components/client-tabs/ContactsTab';
import AssetsTab from '../components/client-tabs/AssetsTab';
import RequestTemplatesTab from '../components/client-tabs/RequestTemplatesTab';
import OfficeLayouts from './OfficeLayouts';

const ClientDetails = ({ setAuth }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(location.state?.activeTab || 'overview');
  const [companiesHouseData, setCompaniesHouseData] = useState(null);
  const [loadingCompaniesHouse, setLoadingCompaniesHouse] = useState(false);
  const [domainResults, setDomainResults] = useState([]);
  const [loadingDomains, setLoadingDomains] = useState(false);
  const [emailResults, setEmailResults] = useState([]);
  const [loadingEmails, setLoadingEmails] = useState(false);
  const [selectedDirector, setSelectedDirector] = useState('');
  const [saving, setSaving] = useState(false);
  const [creatingProspect, setCreatingProspect] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [loadingQuotes, setLoadingQuotes] = useState(false);

  useEffect(() => {
    fetchClient();
    fetchQuotes();
  }, [id]);

  const fetchClient = async () => {
    try {
      const response = await api.get(`/clients/${id}`);
      setClient(response.data);
    } catch (error) {
      toast.error('Failed to load client details');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchQuotes = async () => {
    setLoadingQuotes(true);
    try {
      // Get all quotes and all prospects
      const [quotesRes, prospectsRes] = await Promise.all([
        api.get('/quotes'),
        api.get('/prospects')
      ]);
      
      // Find if this client was converted from a prospect (by ID)
      const convertedProspect = prospectsRes.data.prospects.find(
        p => p.converted_to_client_id === id
      );
      
      // Also find prospects with matching company name (for "New Prospect" clients)
      const sameNameProspects = client ? prospectsRes.data.prospects.filter(
        p => p.company_name.toLowerCase().trim() === client.company_name.toLowerCase().trim()
      ) : [];
      
      // Get all relevant prospect IDs
      const prospectIds = [
        id, // Direct client ID
        convertedProspect?.id, // Converted prospect ID
        ...sameNameProspects.map(p => p.id) // Same name prospects
      ].filter(Boolean);
      
      // Filter quotes that match any of these IDs
      const clientQuotes = quotesRes.data.quotes.filter(q => 
        prospectIds.includes(q.prospect_id)
      );
      
      console.log('Client ID:', id);
      console.log('Found prospect IDs:', prospectIds);
      console.log('Found quotes:', clientQuotes.length);
      
      setQuotes(clientQuotes);
    } catch (error) {
      console.error('Failed to load quotes:', error);
    } finally {
      setLoadingQuotes(false);
    }
  };


  const fetchCompaniesHouseData = async () => {
    if (!client) {
      toast.error('Client data not loaded');
      return;
    }
    
    if (!client.company_number || client.company_number.trim() === '') {
      toast.error('Please enter a company number first');
      return;
    }
    
    setLoadingCompaniesHouse(true);

    try {
      const response = await api.get(`/companies-house/${client.company_number.trim()}`);
      setCompaniesHouseData(response.data);
      
      // Auto-update client with fetched data
      const address = response.data.registered_office_address;
      const addressStr = `${address.address_line_1 || ''} ${address.address_line_2 || ''}, ${address.locality || ''}, ${address.postal_code || ''}`.trim();
      
      await api.put(`/clients/${id}`, {
        address: addressStr,
        directors: response.data.officers
      });
      
      fetchClient();
      toast.success('Companies House data loaded successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch Companies House data');
    } finally {
      setLoadingCompaniesHouse(false);
    }
  };

  const checkDomains = async () => {
    if (!client) return;
    setLoadingDomains(true);

    try {
      const response = await api.post('/domain-checker', {
        company_name: client.company_name,
        tlds: ['.co.uk', '.com', '.uk', '.net', '.io']
      });
      setDomainResults(response.data.results);
      toast.success(`Domain check completed! (${response.data.credits_remaining} credits remaining)`);
      
      // Update local user credits
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      user.credits = response.data.credits_remaining;
      localStorage.setItem('user', JSON.stringify(user));
    } catch (error) {
      if (error.response?.status === 402) {
        toast.error('Insufficient credits! Purchase more credits to continue.');
      } else {
        toast.error('Failed to check domains');
      }
    } finally {
      setLoadingDomains(false);
    }
  };

  const addDomainToClient = async (domain) => {
    setSaving(true);
    try {
      await api.put(`/clients/${id}`, { website: domain });
      fetchClient();
      toast.success('Domain added to client!');
    } catch (error) {
      toast.error('Failed to add domain');
    } finally {
      setSaving(false);
    }
  };

  const checkEmails = async () => {
    if (!client || !selectedDirector) {
      toast.error('Please select a director first');
      return;
    }

    setLoadingEmails(true);

    try {
      // Extract domain from website or use company name
      let domain = '';
      if (client.website) {
        domain = client.website.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
      } else if (domainResults.length > 0) {
        domain = domainResults[0].domain;
      } else {
        toast.error('Please add a domain first or run domain checker');
        setLoadingEmails(false);
        return;
      }

      const response = await api.post('/email-checker', {
        full_name: selectedDirector,
        domain: domain
      });
      setEmailResults(response.data.results);
      toast.success(`Email check completed! (${response.data.credits_remaining} credits remaining)`);
      
      // Update local user credits
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      user.credits = response.data.credits_remaining;
      localStorage.setItem('user', JSON.stringify(user));
    } catch (error) {
      if (error.response?.status === 402) {
        toast.error('Insufficient credits! Purchase more credits to continue.');
      } else {
        toast.error('Failed to check emails');
      }
    } finally {
      setLoadingEmails(false);
    }
  };

  const addEmailToClient = async (email) => {
    setSaving(true);
    try {
      await api.put(`/clients/${id}`, { email: email });
      fetchClient();
      toast.success('Email added to client!');
    } catch (error) {
      toast.error('Failed to add email');
    } finally {
      setSaving(false);
    }
  };

  const updateClientField = async (field, value) => {
    try {
      await api.put(`/clients/${id}`, { [field]: value });
      fetchClient();
      toast.success('Client updated successfully!');
    } catch (error) {
      toast.error('Failed to update client');
    }
  };

  const handleCreateProspect = async () => {
    if (!client) return;
    
    setCreatingProspect(true);
    try {
      const response = await api.post(`/prospects/from-client/${id}`);
      toast.success('Prospect created successfully!');
      navigate(`/prospects`);
    } catch (error) {
      console.error('Failed to create prospect:', error);
      toast.error(error.response?.data?.detail || 'Failed to create prospect');
    } finally {
      setCreatingProspect(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!client) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/dashboard')}
                className="border-slate-300"
                data-testid="back-button"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-slate-800">{client.company_name}</h1>
                  <p className="text-sm text-slate-500">Company #{client.company_number}</p>
                </div>
              </div>
            </div>
            <Button
              onClick={handleCreateProspect}
              disabled={creatingProspect}
              className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800"
              data-testid="create-prospect-button"
            >
              {creatingProspect ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Create Prospect
                </>
              )}
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border border-slate-200 p-1 shadow-sm">
            <TabsTrigger value="overview" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="overview-tab">
              Overview
            </TabsTrigger>
            <TabsTrigger value="contacts" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="contacts-tab">
              <Users className="w-4 h-4 mr-2" />
              Contacts
            </TabsTrigger>
            <TabsTrigger value="assets" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="assets-tab">
              <HardDrive className="w-4 h-4 mr-2" />
              Assets
            </TabsTrigger>
            <TabsTrigger value="layouts" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              <Grid3x3 className="w-4 h-4 mr-2" />
              Layouts
            </TabsTrigger>
            <TabsTrigger value="templates" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="templates-tab">
              Request Templates
            </TabsTrigger>
            <TabsTrigger value="quotes" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              <DollarSign className="w-4 h-4 mr-1" />
              Quotes
            </TabsTrigger>
            <TabsTrigger value="domain" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="domain-tab">
              Domain Checker
            </TabsTrigger>
            <TabsTrigger value="email" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="email-tab">
              Email Checker
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <Card className="shadow-sm border-slate-200">
              <CardHeader>
                <CardTitle>Company Information</CardTitle>
                <CardDescription>Basic details about the client</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="company-name">Company Name</Label>
                    <Input
                      id="company-name"
                      defaultValue={client.company_name}
                      onBlur={(e) => updateClientField('company_name', e.target.value)}
                      className="border-slate-300"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company-number">Company Number</Label>
                    <Input
                      id="company-number"
                      defaultValue={client.company_number}
                      onBlur={(e) => updateClientField('company_number', e.target.value)}
                      className="border-slate-300"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="website">Website</Label>
                    <div className="flex items-center space-x-2">
                      <Globe className="w-5 h-5 text-slate-400" />
                      <Input
                        id="website"
                        placeholder="https://example.com"
                        defaultValue={client.website || ''}
                        onBlur={(e) => updateClientField('website', e.target.value)}
                        className="border-slate-300"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <div className="flex items-center space-x-2">
                      <Mail className="w-5 h-5 text-slate-400" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="contact@example.com"
                        defaultValue={client.email || ''}
                        onBlur={(e) => updateClientField('email', e.target.value)}
                        className="border-slate-300"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <div className="flex items-center space-x-2">
                      <Phone className="w-5 h-5 text-slate-400" />
                      <Input
                        id="phone"
                        placeholder="+44 20 1234 5678"
                        defaultValue={client.phone || ''}
                        onBlur={(e) => updateClientField('phone', e.target.value)}
                        className="border-slate-300"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="address">Address</Label>
                    <div className="flex items-center space-x-2">
                      <MapPin className="w-5 h-5 text-slate-400" />
                      <Input
                        id="address"
                        placeholder="123 Street, City, Postcode"
                        defaultValue={client.address || ''}
                        onBlur={(e) => updateClientField('address', e.target.value)}
                        className="border-slate-300"
                      />
                    </div>
                  </div>
                </div>

                <div className="pt-4">
                  <Button
                    onClick={fetchCompaniesHouseData}
                    disabled={loadingCompaniesHouse || !client?.company_number || client.company_number.trim() === ''}
                    className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
                    data-testid="fetch-companies-house-button"
                  >
                    {loadingCompaniesHouse ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      <>
                        <Search className="w-4 h-4 mr-2" />
                        {!client?.company_number || client.company_number.trim() === '' ? 'Enter Company Number First' : 'Fetch from Companies House'}
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Billing Settings */}
            <Card className="shadow-sm border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5" />
                  Billing Settings
                </CardTitle>
                <CardDescription>Configure billing rates for time tracking</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="hourly-rate">Hourly Rate</Label>
                    <div className="flex items-center space-x-2">
                      <span className="text-slate-500">
                        {client.currency === 'USD' ? '$' : client.currency === 'EUR' ? '€' : '£'}
                      </span>
                      <Input
                        id="hourly-rate"
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        defaultValue={client.hourly_rate || ''}
                        onBlur={(e) => updateClientField('hourly_rate', parseFloat(e.target.value) || 0)}
                        className="border-slate-300"
                      />
                    </div>
                    <p className="text-xs text-slate-500">Rate charged per hour for billable work</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="billing-increment">Billing Increment</Label>
                    <select
                      id="billing-increment"
                      defaultValue={client.billing_increment || 15}
                      onChange={(e) => updateClientField('billing_increment', parseInt(e.target.value))}
                      className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm"
                    >
                      <option value={6}>6 minutes (0.1 hr)</option>
                      <option value={15}>15 minutes (0.25 hr)</option>
                      <option value={30}>30 minutes (0.5 hr)</option>
                      <option value={60}>60 minutes (1 hr)</option>
                    </select>
                    <p className="text-xs text-slate-500">Round up time to this increment</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="currency">Currency</Label>
                    <select
                      id="currency"
                      defaultValue={client.currency || 'GBP'}
                      onChange={(e) => updateClientField('currency', e.target.value)}
                      className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm"
                    >
                      <option value="GBP">GBP (£)</option>
                      <option value="USD">USD ($)</option>
                      <option value="EUR">EUR (€)</option>
                    </select>
                    <p className="text-xs text-slate-500">Currency for invoicing</p>
                  </div>
                </div>
                {client.hourly_rate > 0 && (
                  <div className="p-3 bg-green-50 rounded-lg border border-green-200 text-sm text-green-800">
                    <strong>Billing Active:</strong> Time tracked on tickets for this client will be calculated at{' '}
                    {client.currency === 'USD' ? '$' : client.currency === 'EUR' ? '€' : '£'}
                    {(client.hourly_rate || 0).toFixed(2)}/hour, billed in {client.billing_increment || 15} minute increments.
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Directors */}
            {client.directors && client.directors.length > 0 && (
              <Card className="shadow-sm border-slate-200">
                <CardHeader>
                  <CardTitle>Directors</CardTitle>
                  <CardDescription>Company officers from Companies House</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {client.directors.map((director, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                        <div className="flex items-center space-x-3">
                          <User className="w-5 h-5 text-slate-400" />
                          <div>
                            <p className="font-medium text-slate-800">{director.name}</p>
                            <p className="text-sm text-slate-500">{director.officer_role}</p>
                          </div>
                        </div>
                        {director.date_of_birth && (
                          <Badge variant="outline" className="text-xs">
                            {new Date(director.date_of_birth?.year, director.date_of_birth?.month - 1).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Contacts Tab */}
          <TabsContent value="contacts" className="space-y-6">
            <ContactsTab clientId={id} />
          </TabsContent>

          {/* Assets Tab */}
          <TabsContent value="assets" className="space-y-6">
            <AssetsTab clientId={id} />
          </TabsContent>

          {/* Office Layouts Tab */}
          <TabsContent value="layouts">
            <OfficeLayouts clientId={id} />
          </TabsContent>

          {/* Request Templates Tab */}
          <TabsContent value="templates" className="space-y-6">
            <RequestTemplatesTab clientId={id} />
          </TabsContent>

          {/* Domain Checker Tab */}
          <TabsContent value="domain" className="space-y-6">
            <Card className="shadow-sm border-slate-200">
              <CardHeader>
                <CardTitle>Domain Checker</CardTitle>
                <CardDescription>
                  Check potential domain names for this company
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <Button
                    onClick={checkDomains}
                    disabled={loadingDomains}
                    className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
                    data-testid="check-domains-button"
                  >
                    {loadingDomains ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Checking...
                      </>
                    ) : (
                      <>
                        <Search className="w-4 h-4 mr-2" />
                        Check Domains (1 Credit)
                      </>
                    )}
                  </Button>
                </div>

                {domainResults.length > 0 && (
                  <div className="mt-6 space-y-3">
                    {domainResults.map((result, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-4 bg-white rounded-lg border border-slate-200 hover:border-blue-300"
                      >
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            <span className="font-medium text-slate-800">{result.domain}</span>
                            <div className="flex space-x-2">
                              {result.dns_exists && (
                                <Badge className="bg-green-100 text-green-700 border-green-200">DNS</Badge>
                              )}
                              {result.mx_exists && (
                                <Badge className="bg-blue-100 text-blue-700 border-blue-200">MX</Badge>
                              )}
                              {result.http_status && (
                                <Badge className="bg-purple-100 text-purple-700 border-purple-200">
                                  HTTP {result.http_status}
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="mt-1 text-sm text-slate-500">
                            Score: {result.score} {result.final_url && `• ${result.final_url}`}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => addDomainToClient(result.domain)}
                          disabled={saving}
                          className="bg-blue-600 hover:bg-blue-700"
                          data-testid={`add-domain-${index}`}
                        >
                          Add
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>


          {/* Quotes Tab */}
          <TabsContent value="quotes" className="space-y-6">
            <Card className="shadow-sm border-slate-200">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Quotes</CardTitle>
                    <CardDescription>
                      All quotes generated for this client
                    </CardDescription>
                  </div>
                  <Button
                    onClick={() => navigate(`/generate-quote/${id}`)}
                    className="bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800"
                  >
                    <DollarSign className="w-4 h-4 mr-2" />
                    Generate New Quote
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loadingQuotes ? (
                  <div className="text-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-2" />
                    <p className="text-slate-500">Loading quotes...</p>
                  </div>
                ) : quotes.length === 0 ? (
                  <div className="text-center py-12">
                    <DollarSign className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500 mb-4">No quotes created yet</p>
                    <Button
                      onClick={() => navigate(`/generate-quote/${id}`)}
                      className="bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800"
                    >
                      <DollarSign className="w-4 h-4 mr-2" />
                      Create First Quote
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {quotes.map((quote) => {
                      const getStatusColor = (status) => {
                        switch (status) {
                          case 'draft': return 'bg-slate-100 text-slate-800';
                          case 'sent': return 'bg-blue-100 text-blue-800';
                          case 'accepted': return 'bg-green-100 text-green-800';
                          case 'rejected': return 'bg-red-100 text-red-800';
                          default: return 'bg-slate-100 text-slate-800';
                        }
                      };

                      return (
                        <div
                          key={quote.id}
                          className="border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                          onClick={() => navigate(`/quotes/${quote.id}`)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-3 mb-2">
                                <h3 className="font-semibold text-slate-800">{quote.quote_number}</h3>
                                <Badge className={`${getStatusColor(quote.status)} capitalize`}>
                                  {quote.status}
                                </Badge>
                              </div>
                              <div className="text-sm text-slate-600 space-y-1">
                                <p>Created: {new Date(quote.created_at).toLocaleDateString('en-GB', {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric'
                                })}</p>
                                {quote.sent_at && (
                                  <p>Sent: {new Date(quote.sent_at).toLocaleDateString('en-GB', {
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric'
                                  })}</p>
                                )}
                                {quote.sent_to_email && (
                                  <p className="flex items-center text-blue-600">
                                    <Mail className="w-3 h-3 mr-1" />
                                    {quote.sent_to_email}
                                  </p>
                                )}
                                <p className="text-xs text-slate-500">
                                  {quote.services.length} service{quote.services.length !== 1 ? 's' : ''}
                                </p>
                              </div>
                            </div>
                            <div className="text-right ml-4">
                              <p className="text-2xl font-bold text-emerald-600">
                                £{quote.total.toFixed(2)}
                              </p>
                              <p className="text-xs text-slate-500">per month</p>
                              <Button
                                variant="outline"
                                size="sm"
                                className="mt-2"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/quotes/${quote.id}`);
                                }}
                              >
                                <Eye className="w-3 h-3 mr-1" />
                                View
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>


          {/* Email Checker Tab */}
          <TabsContent value="email" className="space-y-6">
            <Card className="shadow-sm border-slate-200">
              <CardHeader>
                <CardTitle>Email Checker</CardTitle>
                <CardDescription>
                  Check potential email addresses for directors
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="director-select">Select Director</Label>
                  <select
                    id="director-select"
                    data-testid="director-select"
                    value={selectedDirector}
                    onChange={(e) => setSelectedDirector(e.target.value)}
                    className="w-full h-11 px-3 rounded-md border border-slate-300 bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">-- Select a director --</option>
                    {client.directors?.map((director, index) => (
                      <option key={index} value={director.name}>
                        {director.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <Button
                    onClick={checkEmails}
                    disabled={loadingEmails || !selectedDirector}
                    className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
                    data-testid="check-emails-button"
                  >
                    {loadingEmails ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Checking...
                      </>
                    ) : (
                      <>
                        <Search className="w-4 h-4 mr-2" />
                        Check Emails (1 Credit)
                      </>
                    )}
                  </Button>
                </div>

                {emailResults.length > 0 && (
                  <div className="mt-6 space-y-3">
                    {emailResults.map((result, index) => {
                      const statusIcon = result.status === 'ACCEPTED' ? (
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      ) : result.status === 'REJECTED' ? (
                        <XCircle className="w-5 h-5 text-red-600" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-yellow-600" />
                      );

                      const statusColor = result.status === 'ACCEPTED'
                        ? 'bg-green-100 text-green-700 border-green-200'
                        : result.status === 'REJECTED'
                        ? 'bg-red-100 text-red-700 border-red-200'
                        : 'bg-yellow-100 text-yellow-700 border-yellow-200';

                      return (
                        <div
                          key={index}
                          className="flex items-center justify-between p-4 bg-white rounded-lg border border-slate-200"
                        >
                          <div className="flex-1">
                            <div className="flex items-center space-x-3">
                              {statusIcon}
                              <span className="font-medium text-slate-800">{result.email}</span>
                              <Badge className={statusColor}>{result.status}</Badge>
                            </div>
                            <p className="mt-1 text-sm text-slate-500 line-clamp-1">{result.message}</p>
                          </div>
                          {result.status === 'ACCEPTED' && (
                            <Button
                              size="sm"
                              onClick={() => addEmailToClient(result.email)}
                              disabled={saving}
                              className="bg-blue-600 hover:bg-blue-700"
                              data-testid={`add-email-${index}`}
                            >
                              Add
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default ClientDetails;
